import { useInitUserQuery } from "graphql/user";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { GetUserQuery } from "types";

const PUBLIC_ROUTES = [
  "sign-in",
  "sign-up",
  "forgot-password",
  "password-reset",
  "oauth",
  "saml",
  "email-verification",
  "landing",
  "sign-up-new-company-admin",
  "sign-up-employee",
] as const;

export const Reception = () => {
  const { authState } = useAuth();
  const tokensLoading = authState.status === AuthStatus.Pending;

  const { data, loading: rolesLoading } = useInitUserQuery({
    skip: authState.status !== AuthStatus.Authenticated,
  });

  const { language } = useParams();
  const { pathname, search, state } = useLocation();

  const userRoles: GetUserQuery["user"]["userRoles"] =
    data?.user?.userRoles ?? [];
  const employments = data?.user?.employments ?? [];

  const hasAdminRoles = userRoles.some(
    (userRole) => userRole.role.isPlatform || userRole.company != null,
  );
  const hasEmployeeRoles = employments.length > 0;

  const pathInfo = useMemo(() => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const languageIndex = pathSegments.findIndex((seg) => seg === language);
    const afterLanguage = pathSegments.slice(languageIndex + 1);
    const firstSegmentAfterLanguage = afterLanguage[0];

    const currentApp = firstSegmentAfterLanguage;
    const isOnAdmin = currentApp === "admin";
    const isOnEmployee = currentApp === "employee";
    const hasAppPrefix = isOnAdmin || isOnEmployee;
    const isPublicRoute = PUBLIC_ROUTES.some((route) =>
      firstSegmentAfterLanguage?.startsWith(route),
    );

    return {
      pathSegments,
      languageIndex,
      afterLanguage,
      firstSegmentAfterLanguage,
      currentApp,
      isOnAdmin,
      isOnEmployee,
      hasAppPrefix,
      isPublicRoute,
    };
  }, [pathname, language]);

  const {
    isOnAdmin,
    isOnEmployee,
    hasAppPrefix,
    isPublicRoute,
    afterLanguage,
  } = pathInfo;

  if (tokensLoading || rolesLoading || !language) {
    return null;
  }

  if (authState.status === AuthStatus.Unauthenticated) {
    let from: string | undefined = undefined;
    const pathSegments = pathname.split("/").filter(Boolean);
    if (pathSegments.length >= 3) {
      from = pathname + search;
    }
    return <Navigate to={`/${language}/sign-in`} state={{ from }} replace />;
  }

  if (isPublicRoute) {
    return <Outlet />;
  }

  if (!hasAppPrefix) {
    let targetApp: "admin" | "employee" = "admin";

    if (hasEmployeeRoles && !hasAdminRoles) {
      targetApp = "employee";
    }

    const newPath = `/${language}/${targetApp}/${afterLanguage.join("/")}`;

    return <Navigate to={newPath + search} replace state={state} />;
  }

  if (hasAdminRoles && !hasEmployeeRoles) {
    if (isOnEmployee) {
      return <Navigate to={`/${language}/admin`} replace state={state} />;
    }
    if (!isOnAdmin && !isOnEmployee) {
      return <Navigate to={`/${language}/admin`} replace state={state} />;
    }
    return <Outlet />;
  }

  if (hasEmployeeRoles && !hasAdminRoles) {
    if (isOnAdmin) {
      return <Navigate to={`/${language}/employee`} replace state={state} />;
    }
    if (!isOnAdmin && !isOnEmployee) {
      return <Navigate to={`/${language}/employee`} replace state={state} />;
    }
    return <Outlet />;
  }

  if (!isOnAdmin && !isOnEmployee) {
    return <Navigate to={`/${language}/admin`} replace state={state} />;
  }

  return <Outlet />;
};
